import { useState, useEffect } from 'react';
import { useAuth } from '@hooks';
import { subscriptionApi, converterApi } from '@services/api';
import type { UsageStats, Subscription, Conversion } from '@types';
import { isUnlimitedTier } from '@/types/subscription';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  DocumentTextIcon,
  ClockIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

// Import dashboard components
import StatisticsCard from '@components/dashboard/StatisticsCard';
import UsageChart from '@components/dashboard/UsageChart';
import RecentActivity from '@components/dashboard/RecentActivity';
import QuickActions from '@components/dashboard/QuickActions';

interface DashboardData {
  usageStats: UsageStats | null;
  subscription: Subscription | null;
  recentConversions: Conversion[];
}

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    usageStats: null,
    subscription: null,
    recentConversions: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel with individual error handling
      const [usageStats, subscription, conversionHistory] =
        await Promise.allSettled([
          subscriptionApi.getUsageStats().catch(() => null),
          subscriptionApi.getCurrentSubscription().catch(() => null),
          converterApi
            .getConversionHistory({ page: 1, per_page: 5 })
            .catch(() => ({ conversions: [], total: 0, page: 1, per_page: 5 })),
        ]);

      // Subscription API returns NIB types (employee-based) - Dashboard now uses these directly
      setDashboardData({
        usageStats: usageStats.status === 'fulfilled' ? usageStats.value : null,
        subscription:
          subscription.status === 'fulfilled' ? subscription.value : null,
        recentConversions:
          conversionHistory.status === 'fulfilled'
            ? (conversionHistory.value?.conversions ?? [])
            : [],
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const handleDownload = async (conversionId: string) => {
    try {
      const blob = await converterApi.downloadConvertedFile(conversionId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conversion_${conversionId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Failed to download file');
    }
  };

  const handleRetry = async (conversionId: string) => {
    try {
      await converterApi.retryConversion(conversionId);
      toast.success('Conversion retry initiated');
      // Refresh dashboard data
      fetchDashboardData();
    } catch (err) {
      console.error('Error retrying conversion:', err);
      toast.error('Failed to retry conversion');
    }
  };

  // Calculate statistics from NIB employee-based usage
  const stats = dashboardData.usageStats?.current_period;

  // Check if unlimited tier
  const unlimited = stats ? isUnlimitedTier(stats.employee_limit) : false;

  // Calculate remaining capacity
  const remainingCapacity = unlimited
    ? -1
    : stats
      ? Math.max(0, stats.employee_limit - stats.employees_processed)
      : 0;

  // Calculate average processing time from recent conversions
  const recentConversions = dashboardData.recentConversions ?? [];
  const avgProcessingTime =
    recentConversions.length > 0
      ? recentConversions
          .filter(c => c.processing_time_seconds)
          .reduce((acc, c) => acc + (c.processing_time_seconds || 0), 0) /
        (recentConversions.filter(c => c.processing_time_seconds).length || 1)
      : 0;

  if (error && !loading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  <div className="mt-2">
                    <button
                      onClick={fetchDashboardData}
                      className="text-sm font-medium text-red-800 hover:text-red-700"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Welcome back, {user?.company_name || user?.email}!
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatisticsCard
            title="Employees Processed"
            value={stats?.employees_processed || 0}
            icon={UsersIcon}
            description="This period"
            loading={loading}
          />
          <StatisticsCard
            title="Submissions"
            value={stats?.submissions_count || 0}
            icon={DocumentTextIcon}
            description="This period"
            loading={loading}
          />
          <StatisticsCard
            title="Remaining Capacity"
            value={unlimited ? 'Unlimited' : remainingCapacity}
            icon={ClockIcon}
            description={
              unlimited
                ? 'Enterprise tier'
                : `of ${stats?.employee_limit || 0} limit`
            }
            loading={loading}
          />
          <StatisticsCard
            title="Avg Processing Time"
            value={
              avgProcessingTime > 0 ? `${avgProcessingTime.toFixed(1)}s` : '-'
            }
            icon={BoltIcon}
            description="Average speed"
            loading={loading}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Quick Actions
          </h2>
          <QuickActions loading={loading} />
        </div>

        {/* Usage Chart and Recent Activity */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <UsageChart
              data={dashboardData.usageStats?.history || []}
              loading={loading}
            />
          </div>
          <div>
            <RecentActivity
              conversions={recentConversions}
              loading={loading}
              onDownload={handleDownload}
              onRetry={handleRetry}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
